import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PosDeviceContext } from '../context/PosDeviceContext';
import api from '../config/api';
import './AdminShell.css';

export const AdminShellContext = createContext(false);

export function useInAdminShell() {
  return useContext(AdminShellContext);
}

const NAV_SECTIONS = [
  {
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', end: true },
      { to: '/owner', label: 'Owner hub' },
      { to: '/analytics', label: 'Business insights' }
    ]
  },
  {
    title: 'Sales & orders',
    items: [
      { to: '/orders', label: 'Orders' },
      { to: '/order-approvals', label: 'Order approvals' },
      { to: '/orders/create', label: 'Create order' },
      { to: '/store-manager', label: 'Store manager' },
      { to: '/accountant', label: 'Accountant' },
      { to: '/expenses', label: 'POS expenses' },
      { to: '/caterers', label: 'Caterers SOA' }
    ]
  },
  {
    title: 'POS',
    items: [
      { to: '/pos-menus', label: 'Store-wise menus' },
      { to: '/pos-inventory', label: 'Inventory' },
      { to: '/users', label: 'Users & POS links', hash: '#pos-links' },
      // Admin can open live POS without terminal enrollment
      { to: '/pos', label: 'POS register', adminAlways: true, requiresPos: true }
    ]
  },
  {
    title: 'Catalog',
    items: [
      { to: '/flavours', label: 'Flavours' },
      { to: '/desserts', label: 'Desserts' },
      { to: '/cup-cakes', label: 'Cup cakes' },
      { to: '/plants', label: 'Plants' },
      { to: '/agents', label: 'Agents' },
      { to: '/grazing-tables', label: 'Grazing tables' },
      { to: '/catering-menu', label: 'Catering menu' },
      { to: '/catering-planner', label: 'CaterCraft' }
    ]
  },
  {
    title: 'Admin',
    items: [{ to: '/users', label: 'Users & stores' }]
  }
];

export default function AdminShell({ children }) {
  const { user, logout } = useContext(AuthContext);
  const { enrolled: posDeviceEnrolled } = useContext(PosDeviceContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [posStores, setPosStores] = useState([]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/stores');
        if (!cancelled) setPosStores(data.stores || []);
      } catch {
        if (!cancelled) setPosStores([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => !item.requiresPos || posDeviceEnrolled || item.adminAlways
        )
      })).filter((section) => section.items.length > 0),
    [posDeviceEnrolled]
  );

  const pageTitle = useMemo(() => {
    const all = sections.flatMap((s) => s.items);
    const match = all.find((item) =>
      item.end ? location.pathname === item.to : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
    );
    return match?.label || 'Admin';
  }, [location.pathname, sections]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <AdminShellContext.Provider value={true}>
      <div className={`admin-shell${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}>
        <div className="admin-shell-backdrop" onClick={() => setMobileOpen(false)} aria-hidden={!mobileOpen} />

        <aside className="admin-shell-sidebar" aria-label="Admin navigation">
          <div className="admin-shell-brand">
            <Link to="/" className="admin-shell-brand-link">
              <img src="/cakexpress-logo.png" alt="" className="admin-shell-logo" />
              {!collapsed ? (
                <span className="admin-shell-brand-text">
                  <strong>CakeXpress</strong>
                  <small>Admin console</small>
                </span>
              ) : null}
            </Link>
            <button
              type="button"
              className="admin-shell-collapse"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? '»' : '«'}
            </button>
          </div>

          <nav className="admin-shell-nav">
            {sections.map((section) => (
              <div key={section.title} className="admin-shell-section">
                {!collapsed ? <p className="admin-shell-section-title">{section.title}</p> : null}
                <ul>
                  {section.items.map((item) => (
                    <li key={`${item.to}-${item.label}`}>
                      <NavLink
                        to={item.to}
                        end={Boolean(item.end)}
                        className={({ isActive }) => `admin-shell-link${isActive ? ' is-active' : ''}`}
                        title={item.label}
                      >
                        <span className="admin-shell-link-dot" aria-hidden />
                        {!collapsed ? <span>{item.label}</span> : null}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="admin-shell-footer">
            {!collapsed ? (
              <div className="admin-shell-user">
                <strong>{user?.full_name || user?.username || 'Admin'}</strong>
                <span>{user?.role}</span>
              </div>
            ) : null}
            <button type="button" className="admin-shell-logout" onClick={handleLogout}>
              {collapsed ? '⎋' : 'Log out'}
            </button>
          </div>
        </aside>

        <div className="admin-shell-main">
          <header className="admin-shell-topbar">
            <button
              type="button"
              className="admin-shell-mobile-toggle"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
            <div className="admin-shell-topbar-title">
              <h1>{pageTitle}</h1>
            </div>
            <div className="admin-shell-topbar-actions">
              {posStores.map((s) => (
                <Link
                  key={s.id}
                  to={`/pos?storeId=${s.id}`}
                  className="admin-shell-pill"
                  title={`Open live POS for ${s.name}`}
                >
                  POS {s.name}
                </Link>
              ))}
              {!posStores.length ? (
                <Link to="/pos" className="admin-shell-pill">
                  Open POS
                </Link>
              ) : null}
              <Link to="/pos-menus" className="admin-shell-pill admin-shell-pill--gold">
                Store-wise menus
              </Link>
            </div>
          </header>
          <div className="admin-shell-content">{children}</div>
        </div>
      </div>
    </AdminShellContext.Provider>
  );
}
